import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { CtaSection } from "@/components/CtaSection";
import { ResultsGallerySection } from "@/components/ResultsGallery";
import { IMG, SERVICES } from "@/lib/content";
import { useSeo } from "@/hooks/useSeo";

const residential = SERVICES.find((s) => s.id === "residential");

const plans = [
  { title: "Regular Home Cleaning", desc: "Weekly, fortnightly or monthly visits that keep your home consistently fresh — so you get your weekends back." },
  { title: "One-Off & Deep Cleaning", desc: "A thorough top-to-bottom clean for spring resets, special occasions or when your home needs extra attention." },
  { title: "Move-In / Move-Out", desc: "Detailed cleaning to prepare a property for its next chapter — perfect presentation, inside and out." },
];

export default function Residential() {
  useSeo("House & Residential Cleaning Perth | RD Cleaning Services", "Regular house cleaning, deep cleaning and apartment cleaning across Perth. Practical residential cleaning solutions for homes of every size and budget.");
  return (
    <>
      <PageHero
        eyebrow="Residential Cleaning Perth"
        title="A Cleaner Home. More Time for You."
        subtitle="Whether you need a regular cleaner, a deep clean or help preparing your property for a move, RD Cleaning Services provides practical cleaning solutions for homes and apartments across Perth."
        image={IMG.living}
      >
        <Link to="/quote" className="focus-ring inline-flex items-center gap-2 rounded-full bg-teal-500 px-7 py-3.5 text-sm font-bold text-navy-deep transition-all hover:-translate-y-0.5 hover:bg-teal-400" data-testid="residential-hero-quote-button">
          Book a Home Cleaning <ArrowRight className="h-4 w-4" />
        </Link>
      </PageHero>

      <section className="bg-white py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-5 lg:grid-cols-3">
            {plans.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.08}>
                <div className="h-full rounded-2xl border border-slate-200 bg-slate-50/60 p-8 transition-all hover:-translate-y-1 hover:bg-white hover:shadow-lg" data-testid={`residential-plan-${i}`}>
                  <span className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-teal-600">{String(i + 1).padStart(2, "0")}</span>
                  <h2 className="mt-3 font-display text-xl font-bold text-navy">{p.title}</h2>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-20 sm:py-28">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <Reveal>
            <p className="eyebrow">What's Included</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
              Every room, every detail.
            </h2>
            <p className="mt-5 leading-relaxed text-slate-600">
              From kitchens and bathrooms to bedrooms and living areas, our residential cleaning covers the
              spaces you use every day — with the flexibility to focus on what matters most to you.
            </p>
            <ul className="mt-7 grid grid-cols-1 gap-x-6 gap-y-2.5 sm:grid-cols-2">
              {residential.items.map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm text-slate-700">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-teal-600" /> {item}
                </li>
              ))}
            </ul>
            <Link to="/quote" className="focus-ring mt-9 inline-flex items-center gap-2 rounded-full bg-teal-600 px-7 py-3.5 text-sm font-bold text-white shadow-lg shadow-teal-600/25 transition-all hover:-translate-y-0.5 hover:bg-teal-700" data-testid="residential-quote-button">
              Book a Home Cleaning <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="grid gap-5">
              <div className="overflow-hidden rounded-3xl shadow-xl">
                <img src={IMG.kitchen} alt="Freshly cleaned modern kitchen in a Perth home" loading="lazy" className="aspect-[16/10] w-full object-cover" />
              </div>
              <div className="overflow-hidden rounded-3xl shadow-xl">
                <img src={IMG.bathroom} alt="Spotless modern bathroom after a professional clean" loading="lazy" className="aspect-[16/10] w-full object-cover" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <ResultsGallerySection
        slugs={["kitchen", "bathroom", "windows", "strata-lobby", "post-construction"]}
        heading="Before & After in Homes and Apartments"
        text="Floors, glass and finishes brought back to their best — shown side by side."
        bg="bg-white"
        testid="residential-gallery-section"
      />

      <CtaSection heading="Ready for a Fresher Home?" text="Tell us about your home and we'll recommend a cleaning plan that fits your routine and your budget." />
    </>
  );
}
